package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsPowerPlayAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apTerminal: N,
    override val apGround: N,
    override val apLow: N,
    override val apMedium: N,
    override val apHigh: N,
    override val apPark: N,
    override val apNavigation: N,
    override val tpTerminalNear: N,
    override val tpTerminalFar: N,
    override val tpGround: N,
    override val tpLow: N,
    override val tpMedium: N,
    override val tpHigh: N,
    override val epOwnership: N,
    override val epCircuit: N,
    override val epPark: N,
) : MetricsAPI<N>, MetricsPowerPlay<N> {
    constructor(team1: MetricsPowerPlay<N>, team2: MetricsPowerPlay<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apTerminal = team1.apTerminal + team2.apTerminal,
        apGround = team1.apGround + team2.apGround,
        apLow = team1.apLow + team2.apLow,
        apMedium = team1.apMedium + team2.apMedium,
        apHigh = team1.apHigh + team2.apHigh,
        apPark = team1.apPark + team2.apPark,
        apNavigation = team1.apNavigation + team2.apNavigation,
        tpTerminalNear = team1.tpTerminalNear + team2.tpTerminalNear,
        tpTerminalFar = team1.tpTerminalFar + team2.tpTerminalFar,
        tpGround = team1.tpGround + team2.tpGround,
        tpLow = team1.tpLow + team2.tpLow,
        tpMedium = team1.tpMedium + team2.tpMedium,
        tpHigh = team1.tpHigh + team2.tpHigh,
        epOwnership = team1.epOwnership + team2.epOwnership,
        epCircuit = team1.epCircuit + team2.epCircuit,
        epPark = team1.epPark + team2.epPark,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsPowerPlay<*>, team2: MetricsPowerPlay<*>) = MetricsPowerPlayAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apTerminal = team1.apTerminal.convert<N>() + team2.apTerminal.convert<N>(),
            apGround = team1.apGround.convert<N>() + team2.apGround.convert<N>(),
            apLow = team1.apLow.convert<N>() + team2.apLow.convert<N>(),
            apMedium = team1.apMedium.convert<N>() + team2.apMedium.convert<N>(),
            apHigh = team1.apHigh.convert<N>() + team2.apHigh.convert<N>(),
            apPark = team1.apPark.convert<N>() + team2.apPark.convert<N>(),
            apNavigation = team1.apNavigation.convert<N>() + team2.apNavigation.convert<N>(),
            tpTerminalNear = team1.tpTerminalNear.convert<N>() + team2.tpTerminalNear.convert<N>(),
            tpTerminalFar = team1.tpTerminalFar.convert<N>() + team2.tpTerminalFar.convert<N>(),
            tpGround = team1.tpGround.convert<N>() + team2.tpGround.convert<N>(),
            tpLow = team1.tpLow.convert<N>() + team2.tpLow.convert<N>(),
            tpMedium = team1.tpMedium.convert<N>() + team2.tpMedium.convert<N>(),
            tpHigh = team1.tpHigh.convert<N>() + team2.tpHigh.convert<N>(),
            epOwnership = team1.epOwnership.convert<N>() + team2.epOwnership.convert<N>(),
            epCircuit = team1.epCircuit.convert<N>() + team2.epCircuit.convert<N>(),
            epPark = team1.epPark.convert<N>() + team2.epPark.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsPowerPlay<*>.convert() = serialize { convert<N>() }

fun <N : Number> MetricsPowerPlay<N>.serialize() = MetricsPowerPlayAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apTerminal = apTerminal,
    apGround = apGround,
    apLow = apLow,
    apMedium = apMedium,
    apHigh = apHigh,
    apPark = apPark,
    apNavigation = apNavigation,
    tpTerminalNear = tpTerminalNear,
    tpTerminalFar = tpTerminalFar,
    tpGround = tpGround,
    tpLow = tpLow,
    tpMedium = tpMedium,
    tpHigh = tpHigh,
    epOwnership = epOwnership,
    epCircuit = epCircuit,
    epPark = epPark,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsPowerPlay<T>.serialize(serializer: T.() -> R) = MetricsPowerPlayAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apTerminal = apTerminal.serializer(),
    apGround = apGround.serializer(),
    apLow = apLow.serializer(),
    apMedium = apMedium.serializer(),
    apHigh = apHigh.serializer(),
    apPark = apPark.serializer(),
    apNavigation = apNavigation.serializer(),
    tpTerminalNear = tpTerminalNear.serializer(),
    tpTerminalFar = tpTerminalFar.serializer(),
    tpGround = tpGround.serializer(),
    tpLow = tpLow.serializer(),
    tpMedium = tpMedium.serializer(),
    tpHigh = tpHigh.serializer(),
    epOwnership = epOwnership.serializer(),
    epCircuit = epCircuit.serializer(),
    epPark = epPark.serializer(),
)

@JvmName("serializeDouble")
fun MetricsPowerPlay<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsPowerPlay<Number>.serialize() = serialize { toShort() }

fun MetricsPowerPlay<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apTerminal: $apTerminal
        apGround: $apGround
        apLow: $apLow
        apMedium: $apMedium
        apHigh: $apHigh
        apPark: $apPark
        apNavigation: $apNavigation
        tpTerminalNear: $tpTerminalNear
        tpTerminalFar: $tpTerminalFar
        tpGround: $tpGround
        tpLow: $tpLow
        tpMedium: $tpMedium
        tpHigh: $tpHigh
        epOwnership: $epOwnership
        epCircuit: $epCircuit
        epPark: $epPark
    """.trimIndent()

object MetricsPowerPlayMeta : MetricsMeta<MetricsPowerPlay<*>> {
    override val names = linkedMapOf(
        MetricsPowerPlay<*>::total to "Total",
        MetricsPowerPlay<*>::npTotal to "NP Total",
        MetricsPowerPlay<*>::auto to "Auto",
        MetricsPowerPlay<*>::teleop to "Teleop",
        MetricsPowerPlay<*>::endgame to "Endgame",
        MetricsPowerPlay<*>::penalty to "Penalty",
        MetricsPowerPlay<*>::apTerminal to "Terminal",
        MetricsPowerPlay<*>::apGround to "Ground Junction",
        MetricsPowerPlay<*>::apLow to "Low Junction",
        MetricsPowerPlay<*>::apMedium to "Medium Junction",
        MetricsPowerPlay<*>::apHigh to "High Junction",
        MetricsPowerPlay<*>::apPark to "Park",
        MetricsPowerPlay<*>::apNavigation to "Navigation",
        MetricsPowerPlay<*>::tpTerminalNear to "Terminal Near",
        MetricsPowerPlay<*>::tpTerminalFar to "Terminal Far",
        MetricsPowerPlay<*>::tpGround to "Ground",
        MetricsPowerPlay<*>::tpLow to "Low Junction",
        MetricsPowerPlay<*>::tpMedium to "Medium Junction",
        MetricsPowerPlay<*>::tpHigh to "High Junction",
        MetricsPowerPlay<*>::epOwnership to "Ownership",
        MetricsPowerPlay<*>::epCircuit to "Circuit",
        MetricsPowerPlay<*>::epPark to "Park",
    )
    
    override val periods = linkedMapOf(
        MetricsPowerPlay<*>::total to ALL,
        MetricsPowerPlay<*>::npTotal to ALL,
        MetricsPowerPlay<*>::auto to AUTO,
        MetricsPowerPlay<*>::teleop to TELEOP,
        MetricsPowerPlay<*>::endgame to ENDGAME,
        MetricsPowerPlay<*>::penalty to ALL,
        MetricsPowerPlay<*>::apTerminal to AUTO,
        MetricsPowerPlay<*>::apGround to AUTO,
        MetricsPowerPlay<*>::apLow to AUTO,
        MetricsPowerPlay<*>::apMedium to AUTO,
        MetricsPowerPlay<*>::apHigh to AUTO,
        MetricsPowerPlay<*>::apPark to AUTO,
        MetricsPowerPlay<*>::apNavigation to AUTO,
        MetricsPowerPlay<*>::tpTerminalNear to TELEOP,
        MetricsPowerPlay<*>::tpTerminalFar to TELEOP,
        MetricsPowerPlay<*>::tpGround to TELEOP,
        MetricsPowerPlay<*>::tpLow to TELEOP,
        MetricsPowerPlay<*>::tpMedium to TELEOP,
        MetricsPowerPlay<*>::tpHigh to TELEOP,
        MetricsPowerPlay<*>::epOwnership to ENDGAME,
        MetricsPowerPlay<*>::epCircuit to ENDGAME,
        MetricsPowerPlay<*>::epPark to ENDGAME,
    )
}
